import { Dispatch } from 'redux';
import types from 'redux/actions/actionTypes';
import { axiosV2 } from 'interceptors/axios';

import { Operation } from 'types';

import { decryptResponse } from 'utils/token';
import { dispatchError } from 'utils/dispatchError';
import { ApiQueryParams } from 'types/ApiQueryParams';
import { postCanjeOperation } from '../api/canjeApi';
import { updatePartnerShares } from '../utils/updatePartnerShares';
import { updateSocietyData } from '../../society/redux/updateSocietyData';

type GetOperationsProps = {
  societyId: string;
  params?: ApiQueryParams;
  signal?: AbortSignal;
};

type UpdateOperationProps = {
  operationId: string;
  updateOperationData: Partial<Operation>;
};

export const addOperation =
  (operationData: Partial<Operation>, dryRun = false) =>
  async (dispatch: Dispatch) => {
    try {
      const body = { ...operationData, dryRun };
      const { data } = await axiosV2.post('/operations', body);
      const decodedData: any = await decryptResponse(data);

      if (dryRun) {
        return decodedData;
      }

      dispatch({
        type: types.CREATE_OPERATION,
        operation: decodedData.operation,
      });

      updatePartnerShares(decodedData, dispatch);
      updateSocietyData(decodedData, dispatch);

      return decodedData;
    } catch (error) {
      return dispatchError(error, dispatch);
    }
  };

type CanjeOperationPart = {
  operation?: Operation;
  partnerActualShares?: Record<string, number>;
  societyShares?: Record<string, unknown>;
  societyPartnersCount?: Record<string, unknown>;
  societyOperationsCount?: Record<string, unknown>;
};

type CanjeDecodedResponse = {
  capitalDecrease?: CanjeOperationPart;
  capitalIncrease?: CanjeOperationPart;
};

export const executeCanje =
  (operationData: Record<string, unknown>) => async (dispatch: Dispatch) => {
    try {
      const decoded = (await postCanjeOperation(
        operationData
      )) as CanjeDecodedResponse;

      const applyPart = (part: CanjeOperationPart | undefined) => {
        if (!part?.operation) return;
        dispatch({
          type: types.CREATE_OPERATION,
          operation: part.operation,
        });
        updatePartnerShares(part as any, dispatch);
        updateSocietyData(part as any, dispatch);
      };

      applyPart(decoded.capitalDecrease);
      applyPart(decoded.capitalIncrease);

      return decoded;
    } catch (error) {
      return dispatchError(error, dispatch);
    }
  };

export const getOperations =
  ({ societyId, params = {}, signal }: GetOperationsProps) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get('/operations', {
        params: {
          societyId,
          ...params,
        },
        // signal,
      });

      const decodedData = await decryptResponse(data);

      dispatch({
        type: types.GET_OPERATIONS,
        operations: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const getOperation =
  (operationId: string, params?: ApiQueryParams) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get(`/operations/${operationId}`, {
        params,
      });
      const decodedData = await decryptResponse(data);

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const updateOperation =
  ({ operationId, updateOperationData }: UpdateOperationProps) =>
  async (dispatch: Dispatch) => {
    const body = { ...updateOperationData };

    const { data } = await axiosV2.put(`/operations/${operationId}`, body);

    const decodedData = await decryptResponse(data);

    dispatch({
      type: types.UPDATE_OPERATION,
      operation: decodedData,
    });
  };

export const deleteOperation =
  (operationId: string) => async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.delete(`/operations/${operationId}`);
      const decodedData = await decryptResponse(data);

      updatePartnerShares(decodedData, dispatch);
      updateSocietyData(decodedData, dispatch);

      dispatch({
        type: types.DELETE_OPERATION,
        operationId,
      });
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };
